import React, { useEffect, useState } from "react";
import { hasRole, isAdminLevel } from "../../lib/roles";
import Paginated from "../../components/Paginated";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../lib/auth";

const emptyScheme = { name: "", duration_months: 24, customer_months: 20, store_months: 4, monthly_amount: 2000, bonus_description: "", active: true };

export default function SchemesAdmin() {
  const { user } = useAuth();
  // Salespersons can only help customers JOIN a scheme and see the customers
  // they enrolled — creating / editing / deleting schemes stays with
  // super admin & major admin.
  const canManageSchemes = hasRole(user, ["super_admin", "major_admin"]);
  // Salesperson-only view; anyone who is also an administrator keeps the admin view.
  const isSalesperson = hasRole(user, "salesperson") && !isAdminLevel(user);
  const [schemes, setSchemes] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [form, setForm] = useState(emptyScheme);
  const [eForm, setEForm] = useState({ customer_name: "", customer_phone: "", nominee_name: "", nominee_relation: "", nominee_address: "", nominee_phone: "" });
  const [active, setActive] = useState(null);
  const [installAmt, setInstallAmt] = useState(0);
  const [paidBy, setPaidBy] = useState("customer");

  const load = async () => {
    const [s, e] = await Promise.all([api.get("/api/crm/schemes"), api.get("/api/crm/schemes/enrollments")]);
    setSchemes(s.data || []); setEnrollments(e.data || []);
  };
  useEffect(() => { load(); }, []);

  // Live check while typing: customer + store months must equal total
  const monthsOk = (+form.customer_months || 0) + (+form.store_months || 0) === (+form.duration_months || 0);

  const openCreate = () => { setEditingId(null); setForm(emptyScheme); setShowForm(true); };
  const openEdit = (s) => { setEditingId(s.id); setForm({ ...emptyScheme, ...s }); setShowForm(true); };

  const saveScheme = async (ev) => {
    ev.preventDefault();
    if (!monthsOk) { toast.error("Customer months + Swayambhu Gold months must equal the total duration"); return; }
    try {
      if (editingId) { await api.patch(`/api/crm/schemes/${editingId}`, form); toast.success("Scheme updated"); }
      else { await api.post("/api/crm/schemes", form); toast.success("Scheme created"); }
      setShowForm(false); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeScheme = async (s) => {
    if (!window.confirm(`Delete scheme "${s.name}"? Enrollments already made are kept.`)) return;
    try { await api.delete(`/api/crm/schemes/${s.id}`); toast.success("Scheme deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // If the customer is already registered, pull their address instead of
  // making the salesperson type it again.
  const fetchCustomerAddress = async () => {
    if (!/^[6-9]\d{9}$/.test(eForm.customer_phone)) { toast.error("Enter the customer's phone first"); return; }
    try {
      const { data } = await api.get(`/api/crm/customers?q=${eForm.customer_phone}`);
      const c = (data || []).find((x) => (x.phone || "").endsWith(eForm.customer_phone));
      const addr = c?.address || c?.location || "";
      if (addr) { setEForm((f) => ({ ...f, nominee_address: addr })); toast.success("Address filled from the customer record"); }
      else toast.error("No address on file for this customer — please type it");
    } catch { toast.error("Could not fetch the customer's address"); }
  };

  const enroll = async (ev) => {
    ev.preventDefault();
    try {
      await api.post("/api/crm/schemes/enroll", { ...eForm, scheme_id: enrollFor.id });
      toast.success("Enrolled");
      setEnrollFor(null);
      setEForm({ customer_name: "", customer_phone: "", nominee_name: "", nominee_relation: "", nominee_address: "", nominee_phone: "" });
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addInstallment = async () => {
    if (!active || !installAmt) return;
    try {
      const { data } = await api.post("/api/crm/schemes/installment", { enrollment_id: active.id, amount: installAmt, paid_by: paidBy, note: "" });
      toast.success(`Month ${data.month_no} recorded · Payment ID ${data.payment_id}`);
      setInstallAmt(0); setPaidBy("customer");
      // Refresh the open passbook
      const { data: list } = await api.get("/api/crm/schemes/enrollments");
      setEnrollments(list || []);
      setActive((list || []).find((x) => x.id === active.id) || null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div><div className="eyebrow">Golden Harvest</div><h1 className="font-display text-4xl text-maroon">Schemes</h1></div>
        {canManageSchemes && <button onClick={openCreate} className="btn-maroon" data-testid="add-scheme-btn"><Plus size={14}/> New Scheme</button>}
        {isSalesperson && <span className="text-[11px] text-ash">You can enroll customers into schemes and track the customers you helped join — schemes themselves are managed by the admins.</span>}
      </div>

      <section>
        <div className="eyebrow mb-3">Active Schemes</div>
        <div className="grid lg:grid-cols-3 gap-4">
          {schemes.map((s) => (
            <div key={s.id} className="bg-white border border-line p-5" data-testid={`scheme-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="font-display text-2xl text-maroon">{s.name}</div>
                <div className="flex gap-2">
                  {canManageSchemes && <button onClick={() => openEdit(s)} className="text-maroon" title="Edit" data-testid={`edit-scheme-${s.id}`}><Pencil size={14}/></button>}
                  {canManageSchemes && <button onClick={() => removeScheme(s)} className="text-red-600" title="Delete" data-testid={`delete-scheme-${s.id}`}><Trash2 size={14}/></button>}
                </div>
              </div>
              <div className="text-xs text-ash mt-1">{s.duration_months} months total · {fmtINR(s.monthly_amount)} / month</div>
              <div className="text-xs mt-2 flex gap-2">
                <span className="bg-gold/20 text-maroon px-2 py-0.5">Customer pays {s.customer_months ?? s.duration_months} mo</span>
                <span className="bg-maroon/10 text-maroon px-2 py-0.5">Swayambhu pays {s.store_months ?? 0} mo</span>
              </div>
              {s.bonus_description && <div className="text-xs mt-2 text-ash">{s.bonus_description}</div>}
              <button onClick={() => setEnrollFor(s)} className="btn-outline mt-4 text-xs" data-testid={`enroll-${s.id}`}>Enroll a customer</button>
            </div>
          ))}
          {schemes.length === 0 && <div className="text-xs text-ash">{canManageSchemes ? "No schemes yet — create the first one." : "No schemes available yet."}</div>}
        </div>
      </section>

      <section>
        <div className="eyebrow mb-3">{isSalesperson ? "Customers you helped join (through you)" : "Customer Passbooks"}</div>
        <Paginated items={enrollments} pageSize={15} label="enrolments" scroller>
          {(pageRows) => (
        <div className="bg-white border border-line">
          <table className="w-full text-sm">
            <thead className="bg-pearl border-b border-line"><tr>{["Customer", "Phone", "Scheme", "Enrolled By", "Months Paid", "Total Paid", "Status", "Action"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
            <tbody>
              {pageRows.map((e) => (
                <tr key={e.id} className="border-b border-line/60" data-testid={`enrollment-${e.id}`}>
                  <td className="p-3">{e.customer_name}</td>
                  <td className="p-3 text-ash">{e.customer_phone}</td>
                  <td className="p-3">{e.scheme_name}</td>
                  <td className="p-3 text-xs text-ash">{e.created_by_name || "—"}{e.created_by_role ? ` (${e.created_by_role.replace("_", " ")})` : ""}</td>
                  <td className="p-3">{e.installments_paid} / {e.duration_months}</td>
                  <td className="p-3">{fmtINR(e.total_paid)}</td>
                  <td className="p-3"><span className={e.status === "completed" ? "text-green-700" : ""}>{e.status}</span></td>
                  <td className="p-3"><button onClick={() => { setActive(e); setPaidBy("customer"); setInstallAmt(e.monthly_amount || 0); }} className="text-maroon text-xs underline" data-testid={`add-inst-${e.id}`}>Passbook / Add month</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          )}
        </Paginated>
      </section>

      {/* Create / Edit scheme */}
      {showForm && (
        <Modal close={() => setShowForm(false)}>
          <form onSubmit={saveScheme} className="space-y-3">
            <div className="font-display text-2xl text-maroon">{editingId ? "Edit scheme" : "New scheme"}</div>
            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Scheme name</span>
              <input required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="scheme-name"/>
            </label>
            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Monthly amount (₹) — paid every month</span>
              <input type="number" required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.monthly_amount} onChange={(e) => setForm({...form, monthly_amount: +e.target.value})} data-testid="scheme-monthly"/>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <label className="text-sm">
                <span className="eyebrow text-[10px]">Total months</span>
                <input type="number" required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.duration_months} onChange={(e) => setForm({...form, duration_months: +e.target.value})} data-testid="scheme-duration"/>
              </label>
              <label className="text-sm">
                <span className="eyebrow text-[10px]">Customer pays (months)</span>
                <input type="number" required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.customer_months} onChange={(e) => setForm({...form, customer_months: +e.target.value})} data-testid="scheme-customer-months"/>
              </label>
              <label className="text-sm">
                <span className="eyebrow text-[10px]">Swayambhu Gold pays (months)</span>
                <input type="number" required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.store_months} onChange={(e) => setForm({...form, store_months: +e.target.value})} data-testid="scheme-store-months"/>
              </label>
            </div>
            <div className={`text-xs p-2 border ${monthsOk ? "border-green-600 bg-green-50 text-green-800" : "border-red-500 bg-red-50 text-red-700"}`} data-testid="scheme-months-check">
              {monthsOk
                ? `✓ Customer pays ${form.customer_months} months + Swayambhu Gold pays ${form.store_months} months = ${form.duration_months} months total (${fmtINR((form.monthly_amount || 0) * (form.duration_months || 0))} value)`
                : `Customer (${form.customer_months}) + Swayambhu Gold (${form.store_months}) must equal total (${form.duration_months})`}
            </div>
            <textarea placeholder="Bonus description" rows={2} className="w-full border border-line px-3 py-2 bg-pearl" value={form.bonus_description} onChange={(e) => setForm({...form, bonus_description: e.target.value})}/>
            <button className="btn-maroon w-full justify-center" data-testid="submit-scheme">{editingId ? "Save Changes" : "Create"}</button>
          </form>
        </Modal>
      )}

      {enrollFor && (
        <Modal close={() => setEnrollFor(null)}>
          <form onSubmit={enroll} className="space-y-3">
            <div className="font-display text-2xl text-maroon">Enroll in {enrollFor.name}</div>
            <div className="text-xs text-ash">Customer pays {enrollFor.customer_months ?? enrollFor.duration_months} months · Swayambhu Gold pays {enrollFor.store_months ?? 0} months · {fmtINR(enrollFor.monthly_amount)}/month</div>
            <input required placeholder="Customer name" className="w-full border border-line px-3 py-2 bg-pearl" value={eForm.customer_name} onChange={(e) => setEForm({...eForm, customer_name: e.target.value})} data-testid="enroll-name"/>
            <input required placeholder="Customer phone (10 digits)" maxLength={10} className="w-full border border-line px-3 py-2 bg-pearl" value={eForm.customer_phone} onChange={(e) => setEForm({...eForm, customer_phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="enroll-phone"/>

            {/* Nominee — compulsory, since a scheme runs for years */}
            <div className="border-t border-line pt-3">
              <div className="eyebrow text-[10px] mb-2">Nominee (required)</div>
              <div className="space-y-2">
                <input required placeholder="Nominee name" className="w-full border border-line px-3 py-2 bg-pearl"
                  value={eForm.nominee_name} onChange={(e) => setEForm({...eForm, nominee_name: e.target.value})} data-testid="enroll-nominee-name"/>
                <input required placeholder="Relation with nominee (e.g. Son, Wife, Father)" className="w-full border border-line px-3 py-2 bg-pearl"
                  value={eForm.nominee_relation} onChange={(e) => setEForm({...eForm, nominee_relation: e.target.value})} data-testid="enroll-nominee-relation"/>
                <input type="tel" inputMode="numeric" maxLength={10} placeholder="Nominee phone (optional)" className="w-full border border-line px-3 py-2 bg-pearl"
                  value={eForm.nominee_phone} onChange={(e) => setEForm({...eForm, nominee_phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="enroll-nominee-phone"/>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-ash">Nominee address</span>
                  <button type="button" onClick={fetchCustomerAddress} className="text-[11px] text-maroon underline" data-testid="fetch-address">
                    Use customer's address
                  </button>
                </div>
                <textarea required rows={2} placeholder="Nominee address" className="w-full border border-line px-3 py-2 bg-pearl"
                  value={eForm.nominee_address} onChange={(e) => setEForm({...eForm, nominee_address: e.target.value})} data-testid="enroll-nominee-address"/>
              </div>
            </div>

            <button className="btn-maroon w-full justify-center" data-testid="submit-enroll">Enroll</button>
          </form>
        </Modal>
      )}

      {/* Passbook with month-wise payment IDs */}
      {active && (
        <Modal close={() => setActive(null)} wide>
          <div className="space-y-3">
            <div className="font-display text-2xl text-maroon">Passbook — {active.customer_name}</div>
            <div className="text-xs text-ash">
              {active.scheme_name} · Paid {active.installments_paid}/{active.duration_months} months · Customer share {active.customer_months ?? active.duration_months} mo · Swayambhu share {active.store_months ?? 0} mo
            </div>
            {active.installments_paid >= active.duration_months ? (
              <div className="bg-green-50 border border-green-600 text-green-800 text-sm p-3">✓ All {active.duration_months} months paid — scheme completed.</div>
            ) : (
              <div className="flex flex-wrap gap-2 items-end">
                <label className="text-sm flex-1 min-w-[130px]">
                  <span className="eyebrow text-[10px]">Amount (₹)</span>
                  <input type="number" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={installAmt || ""} onChange={(e) => setInstallAmt(+e.target.value)} data-testid="installment-amount"/>
                </label>
                <label className="text-sm">
                  <span className="eyebrow text-[10px]">Paid by</span>
                  <select className="border border-line px-3 py-2 bg-pearl mt-1 block" value={paidBy} onChange={(e) => setPaidBy(e.target.value)} data-testid="installment-paidby">
                    <option value="customer">Received from customer</option>
                    <option value="store">Swayambhu Gold contribution</option>
                  </select>
                </label>
                <button onClick={addInstallment} className="btn-maroon" data-testid="submit-installment">Mark Month {(active.installments_paid || 0) + 1} Paid</button>
              </div>
            )}
            <div className="mt-3">
              <div className="eyebrow mb-2">Month-wise Ledger</div>
              <div className="border border-line max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-pearl border-b border-line sticky top-0"><tr>{["Month", "Payment ID", "Amount", "Paid By", "Date", "Marked By"].map((h) => <th key={h} className="text-left p-2 eyebrow text-[9px]">{h}</th>)}</tr></thead>
                  <tbody>
                    {(active.ledger || []).map((l, i) => (
                      <tr key={l.payment_id || i} className="border-b border-line/60" data-testid={`ledger-${i}`}>
                        <td className="p-2">Month {l.month_no || i + 1}</td>
                        <td className="p-2 font-mono">{l.payment_id || "—"}</td>
                        <td className="p-2 text-maroon font-medium">{fmtINR(l.amount)}</td>
                        <td className="p-2">
                          <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${l.paid_by === "store" ? "bg-maroon/10 text-maroon" : "bg-gold/20 text-ink"}`}>
                            {l.paid_by === "store" ? "Swayambhu Gold" : "Customer"}
                          </span>
                        </td>
                        <td className="p-2 text-ash">{new Date(l.paid_at).toLocaleDateString("en-GB")}</td>
                        <td className="p-2 text-ash">{l.marked_by || l.by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(active.ledger || []).length === 0 && <div className="p-6 text-center text-ash">No payments recorded yet.</div>}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, close, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={close}>
      <div className={`bg-white border border-line p-6 w-full ${wide ? "max-w-2xl" : "max-w-md"} relative max-h-[88vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-3 right-3"><X size={18}/></button>
        {children}
      </div>
    </div>
  );
}
